import JSZip from "jszip";
import { encrypted } from "./Utility";

/**
 * Converts a Base64 string to a Uint8Array
 */
export const base64ToUint8Array = (base64String) => {
  const cleanBase64 = base64String.replace(/^data:application\/pdf;base64,/, "").trim();
  const binaryString = atob(cleanBase64);
  const len = binaryString.length;
  const bytes = new Uint8Array(len);
  for (let i = 0; i < len; i++) {
    bytes[i] = binaryString.charCodeAt(i);
  }
  return bytes;
};

/**
 * Checks if a Uint8Array starts with '%PDF-'
 */
export const isPdfBinary = (uint8Array) => {
  if (!uint8Array || uint8Array.length < 5) return false;
  return (
    uint8Array[0] === 0x25 &&
    uint8Array[1] === 0x50 &&
    uint8Array[2] === 0x44 &&
    uint8Array[3] === 0x46 &&
    uint8Array[4] === 0x2d
  );
};

/**
 * Checks if a Uint8Array starts with PK (Zip / DOCX) magic bytes (0x50, 0x4B, 0x03, 0x04)
 */
export const isDocxOrZip = (uint8Array) => {
  if (!uint8Array || uint8Array.length < 4) return false;
  return (
    uint8Array[0] === 0x50 &&
    uint8Array[1] === 0x4b &&
    uint8Array[2] === 0x03 &&
    uint8Array[3] === 0x04
  );
};

/**
 * Searches for %PDF- and %%EOF in byte array and extracts exact clean PDF slice
 */
export const extractPdfFromBytes = (uint8Array) => {
  if (!uint8Array || uint8Array.length < 5) return null;

  const target = [0x25, 0x50, 0x44, 0x46, 0x2d];
  let startIndex = -1;

  for (let i = 0; i <= Math.min(uint8Array.length - 5, 32768); i++) {
    if (
      uint8Array[i] === target[0] &&
      uint8Array[i + 1] === target[1] &&
      uint8Array[i + 2] === target[2] &&
      uint8Array[i + 3] === target[3] &&
      uint8Array[i + 4] === target[4]
    ) {
      startIndex = i;
      break;
    }
  }

  if (startIndex === -1) return null;

  const eofTarget = [0x25, 0x25, 0x45, 0x4f, 0x46];
  let endIndex = -1;
  for (let i = uint8Array.length - 5; i >= startIndex; i--) {
    if (
      uint8Array[i] === eofTarget[0] &&
      uint8Array[i + 1] === eofTarget[1] &&
      uint8Array[i + 2] === eofTarget[2] &&
      uint8Array[i + 3] === eofTarget[3] &&
      uint8Array[i + 4] === eofTarget[4]
    ) {
      endIndex = i + 5;
      while (endIndex < uint8Array.length && (uint8Array[endIndex] === 10 || uint8Array[endIndex] === 13 || uint8Array[endIndex] === 32)) {
        endIndex++;
      }
      break;
    }
  }

  const finalEndIndex = endIndex !== -1 ? endIndex : uint8Array.length;
  return uint8Array.slice(startIndex, finalEndIndex);
};

/**
 * Parses multipart/form-data or multipart/mixed ArrayBuffer/Uint8Array
 */
export const parseMultipartBuffer = (buffer, contentTypeHeader = "") => {
  const uint8 = buffer instanceof Uint8Array ? buffer : new Uint8Array(buffer);

  let boundary = "";
  if (contentTypeHeader && contentTypeHeader.includes("boundary=")) {
    const match = contentTypeHeader.match(/boundary=(?:"([^"]+)"|([^;]+))/i);
    if (match) {
      boundary = match[1] || match[2];
    }
  }

  if (!boundary) {
    const decoder = new TextDecoder("utf-8");
    const preview = decoder.decode(uint8.slice(0, 500));
    const firstLineMatch = preview.match(/^--([^\r\n]+)/);
    if (firstLineMatch) {
      boundary = firstLineMatch[1].trim();
    }
  }

  const parts = [];
  const decoder = new TextDecoder("utf-8");

  if (boundary) {
    const boundaryBytes = new TextEncoder().encode(`--${boundary}`);

    const findSubarray = (source, target, fromIndex = 0) => {
      outer: for (let i = fromIndex; i <= source.length - target.length; i++) {
        for (let j = 0; j < target.length; j++) {
          if (source[i + j] !== target[j]) continue outer;
        }
        return i;
      }
      return -1;
    };

    let currentIndex = findSubarray(uint8, boundaryBytes);

    while (currentIndex !== -1) {
      const nextIndex = findSubarray(uint8, boundaryBytes, currentIndex + boundaryBytes.length);
      if (nextIndex === -1) break;

      let partStart = currentIndex + boundaryBytes.length;
      if (uint8[partStart] === 13 && uint8[partStart + 1] === 10) partStart += 2;
      else if (uint8[partStart] === 10) partStart += 1;

      let partEnd = nextIndex;
      if (partEnd >= 2 && uint8[partEnd - 2] === 13 && uint8[partEnd - 1] === 10) partEnd -= 2;
      else if (partEnd >= 1 && uint8[partEnd - 1] === 10) partEnd -= 1;

      const partSlice = uint8.slice(partStart, partEnd);

      const headerDelimiterDoubleCRLF = findSubarray(partSlice, new Uint8Array([13, 10, 13, 10]));
      const headerDelimiterDoubleLF = findSubarray(partSlice, new Uint8Array([10, 10]));

      let bodyStartOffset = 0;
      let rawHeadersText = "";

      if (headerDelimiterDoubleCRLF !== -1) {
        rawHeadersText = decoder.decode(partSlice.slice(0, headerDelimiterDoubleCRLF));
        bodyStartOffset = headerDelimiterDoubleCRLF + 4;
      } else if (headerDelimiterDoubleLF !== -1) {
        rawHeadersText = decoder.decode(partSlice.slice(0, headerDelimiterDoubleLF));
        bodyStartOffset = headerDelimiterDoubleLF + 2;
      }

      const bodyBytes = partSlice.slice(bodyStartOffset);
      const nameMatch = rawHeadersText.match(/name="([^"]+)"/i);
      const filenameMatch = rawHeadersText.match(/filename="([^"]+)"/i);
      const typeMatch = rawHeadersText.match(/Content-Type:\s*([^\r\n;]+)/i);

      parts.push({
        headersText: rawHeadersText,
        name: nameMatch ? nameMatch[1] : "",
        filename: filenameMatch ? filenameMatch[1] : "",
        contentType: typeMatch ? typeMatch[1].trim() : "",
        bodyBytes,
      });

      currentIndex = nextIndex;
    }
  }

  return parts;
};

/**
 * Extracts all files inside a ZIP archive using JSZip
 */
export const extractZipContents = async (uint8Array) => {
  try {
    const zip = await JSZip.loadAsync(uint8Array);
    const filesList = [];
    let pdfBlob = null;
    let docxBlob = null;
    let mainFileName = "";

    const fileKeys = Object.keys(zip.files).filter(
      (name) => !name.startsWith("__MACOSX") && !zip.files[name].dir
    );

    for (let i = 0; i < fileKeys.length; i++) {
      const fileName = fileKeys[i];
      const fileEntry = zip.files[fileName];
      const lowerName = fileName.toLowerCase();

      let mimeType = "application/octet-stream";
      let isPdf = false;
      let isDocx = false;

      if (lowerName.endsWith(".pdf")) {
        mimeType = "application/pdf";
        isPdf = true;
      } else if (lowerName.endsWith(".docx")) {
        mimeType = "application/vnd.openxmlformats-officedocument.wordprocessingml.document";
        isDocx = true;
      } else if (lowerName.endsWith(".json")) {
        mimeType = "application/json";
      }

      const fileBuffer = await fileEntry.async("arraybuffer");
      const fileBlob = new Blob([fileBuffer], { type: mimeType });

      if (isPdf && !pdfBlob) {
        pdfBlob = fileBlob;
        mainFileName = fileName;
      }

      if (isDocx && !docxBlob) {
        docxBlob = fileBlob;
        if (!mainFileName) mainFileName = fileName;
      }

      filesList.push({
        id: i + 1,
        name: fileName,
        title: fileName,
        fileName: fileName,
        blob: fileBlob,
        size: fileBlob.size,
        mimeType,
        isPdf,
        isDocx,
      });
    }

    return {
      isZip: true,
      filesList,
      pdfBlob,
      docxBlob,
      mainFileName,
    };
  } catch (err) {
    console.error("❌ [Zip Parser] Failed to parse as ZIP archive:", err);
    return null;
  }
};

/**
 * Comprehensive parser for generate dossier response
 * Handles:
 * 1. ZIP Archives containing DOCX, PDF, or multiple files
 * 2. Raw PDF ArrayBuffer / Blob
 * 3. Multipart / form-data multi-part response with JSON + PDF
 * 4. Encrypted JSON containing Base64 / PDF / Applications
 * 5. Plain JSON containing Base64 / PDF / Applications
 */
export const parseDossierResponse = async (rawData, responseHeaders = {}) => {
  let result = {
    pdfBlob: null,
    pdfUrl: null,
    docxBlob: null,
    zipBlob: null,
    applications: [],
    metadata: null,
    rawText: null,
    isPdf: false,
    isDocx: false,
    isZip: false,
    mainFileName: "",
  };

  try {
    let uint8Array = null;
    let textContent = "";

    if (rawData instanceof ArrayBuffer) {
      uint8Array = new Uint8Array(rawData);
    } else if (rawData instanceof Uint8Array) {
      uint8Array = rawData;
    } else if (rawData instanceof Blob) {
      if (rawData.type === "application/pdf") {
        result.pdfBlob = rawData;
        result.isPdf = true;
        return result;
      }
      uint8Array = new Uint8Array(await rawData.arrayBuffer());
    } else if (typeof rawData === "string") {
      textContent = rawData;
    } else if (typeof rawData === "object" && rawData !== null) {
      result.metadata = rawData;
    }

    if (uint8Array) {
      // 1. Check if raw binary is a ZIP Archive / DOCX
      if (isDocxOrZip(uint8Array) || responseHeaders?.["content-type"]?.includes("zip")) {
        const zipResult = await extractZipContents(uint8Array);
        if (zipResult && zipResult.filesList.length > 0) {
          result.isZip = true;
          result.zipBlob = new Blob([uint8Array], { type: "application/zip" });
          result.applications = zipResult.filesList;
          result.docxBlob = zipResult.docxBlob;
          result.mainFileName = zipResult.mainFileName;

          if (zipResult.pdfBlob) {
            result.pdfBlob = zipResult.pdfBlob;
            result.isPdf = true;
            return result;
          }

          if (zipResult.docxBlob) {
            result.isDocx = true;
            return result;
          }
        }
      }

      // 2. Check if raw binary is a valid PDF directly
      if (isPdfBinary(uint8Array)) {
        result.pdfBlob = new Blob([uint8Array], { type: "application/pdf" });
        result.isPdf = true;
        return result;
      }

      // 3. Try searching for %PDF- slice anywhere in the bytes
      const extractedPdfSlice = extractPdfFromBytes(uint8Array);
      if (extractedPdfSlice) {
        result.pdfBlob = new Blob([extractedPdfSlice], { type: "application/pdf" });
        result.isPdf = true;
        return result;
      }

      // Try decoding text for JSON / Multipart / Encrypted analysis
      try {
        const decoder = new TextDecoder("utf-8");
        textContent = decoder.decode(uint8Array);
      } catch (err) {
        // Non-decodable binary
      }
    }

    // 4. Check if content is Multipart / Multi-form
    const isMultipart =
      (responseHeaders?.["content-type"] && responseHeaders["content-type"].includes("multipart")) ||
      (textContent && textContent.includes("--") && (textContent.includes("Content-Disposition") || textContent.includes("content-disposition")));

    if (isMultipart && uint8Array) {
      const parts = parseMultipartBuffer(uint8Array, responseHeaders?.["content-type"] || "");

      for (let idx = 0; idx < parts.length; idx++) {
        const part = parts[idx];
        const decoder = new TextDecoder("utf-8");
        const cleanPdfBytes = extractPdfFromBytes(part.bodyBytes) || (isPdfBinary(part.bodyBytes) ? part.bodyBytes : null);

        if (cleanPdfBytes) {
          result.pdfBlob = new Blob([cleanPdfBytes], { type: "application/pdf" });
          result.isPdf = true;
        } else if (isDocxOrZip(part.bodyBytes)) {
          const zipRes = await extractZipContents(part.bodyBytes);
          if (zipRes?.pdfBlob) {
            result.pdfBlob = zipRes.pdfBlob;
            result.isPdf = true;
          }
          if (zipRes?.filesList) {
            result.applications = [...result.applications, ...zipRes.filesList];
          }
        } else {
          try {
            const bodyStr = decoder.decode(part.bodyBytes);
            let parsedJson = null;
            try {
              parsedJson = JSON.parse(bodyStr);
            } catch {
              if (bodyStr.includes("encryptedBody")) {
                const encObj = JSON.parse(bodyStr);
                const dec = encrypted.decrypt(encObj.encryptedBody);
                parsedJson = typeof dec === "string" ? JSON.parse(dec) : dec;
              }
            }

            if (parsedJson) {
              if (parsedJson.applications && Array.isArray(parsedJson.applications)) {
                result.applications = parsedJson.applications;
              }
              if (!result.metadata) result.metadata = parsedJson;
            }
          } catch (e) {
            // Non-JSON part
          }
        }
      }

      if (result.isPdf) {
        return result;
      }
    }

    // 5. Check if text is Encrypted string or JSON
    if (textContent || result.metadata) {
      let parsedObj = result.metadata;

      if (textContent && !parsedObj) {
        if (textContent.startsWith("U2FsdGVkX1") || (!textContent.startsWith("{") && !textContent.startsWith("[") && textContent.length > 50)) {
          try {
            const dec = encrypted.decrypt(textContent.trim());
            if (dec && dec !== textContent) {
              try {
                parsedObj = JSON.parse(dec);
              } catch {
                parsedObj = dec;
              }
            }
          } catch (err) {
            // Not decryptable
          }
        }
      }

      if (!parsedObj && textContent) {
        try {
          parsedObj = JSON.parse(textContent);
        } catch {
          // not JSON
        }
      }

      if (parsedObj?.encryptedBody) {
        try {
          const dec = encrypted.decrypt(parsedObj.encryptedBody);
          parsedObj = typeof dec === "string" ? JSON.parse(dec) : dec;
        } catch (decErr) {
          console.error("Failed to decrypt encryptedBody:", decErr);
        }
      }

      if (parsedObj && typeof parsedObj === "object") {
        result.metadata = parsedObj;

        if (parsedObj.applications && Array.isArray(parsedObj.applications)) {
          result.applications = parsedObj.applications;
        }

        const possiblePdfFields = ["pdf", "file", "pdf_base64", "pdfData", "pdf_data", "data", "content", "document"];
        for (const field of possiblePdfFields) {
          const val = parsedObj[field];
          if (typeof val === "string") {
            if (val.startsWith("JVBERi0") || val.startsWith("data:application/pdf;base64,")) {
              const pdfBytes = base64ToUint8Array(val);
              result.pdfBlob = new Blob([pdfBytes], { type: "application/pdf" });
              result.isPdf = true;
              break;
            } else if (val.startsWith("%PDF-")) {
              const enc = new TextEncoder();
              result.pdfBlob = new Blob([enc.encode(val)], { type: "application/pdf" });
              result.isPdf = true;
              break;
            }
          }
        }
      } else if (typeof parsedObj === "string") {
        if (parsedObj.startsWith("JVBERi0") || parsedObj.startsWith("data:application/pdf;base64,")) {
          const pdfBytes = base64ToUint8Array(parsedObj);
          result.pdfBlob = new Blob([pdfBytes], { type: "application/pdf" });
          result.isPdf = true;
        } else if (parsedObj.startsWith("%PDF-")) {
          const enc = new TextEncoder();
          result.pdfBlob = new Blob([enc.encode(parsedObj)], { type: "application/pdf" });
          result.isPdf = true;
        }
      }
    }
  } catch (err) {
    console.error("❌ Error inside parseDossierResponse:", err);
  }

  // Fallback
  if (!result.pdfBlob && rawData && !result.isDocx && !result.isZip) {
    result.pdfBlob = rawData instanceof Blob ? rawData : new Blob([rawData], { type: "application/pdf" });
  }

  return result;
};
